import React from 'react';
import { useQuery } from '@apollo/client';
import { CAREER_LAUNCH_PIPELINE_QUERY } from '../graphql';

const STATUS_STYLE = {
  COMPLETE:   { color: '#10b981', icon: '✅', bg: 'rgba(16,185,129,0.1)' },
  IN_REVIEW:  { color: '#6366f1', icon: '🔍', bg: 'rgba(99,102,241,0.1)' },
  IN_PROGRESS:{ color: '#f59e0b', icon: '⏳', bg: 'rgba(245,158,11,0.1)' },
  PENDING:    { color: 'var(--text-muted)', icon: '○', bg: 'transparent' },
  LOCKED:     { color: 'var(--text-muted)', icon: '🔒', bg: 'transparent' },
  REJECTED:   { color: '#ef4444', icon: '❌', bg: 'rgba(239,68,68,0.1)' },
};

export default function CareerLaunchPipeline({ expertId, groupId }) {
  const { data, loading } = useQuery(CAREER_LAUNCH_PIPELINE_QUERY, {
    variables: { expertId, groupId: groupId || undefined },
    skip: !expertId,
  });

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading pipeline…</div>;
  const pipeline = data?.careerLaunchPipeline;
  if (!pipeline) return null;

  const { stages, overallStatus, percentComplete } = pipeline;
  const overallColor = overallStatus === 'ACTIVE' ? '#10b981' : overallStatus === 'IN_PROGRESS' ? '#6366f1' : 'var(--text-muted)';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-lg)' }}>CareerLaunch Pipeline</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            Your journey from application to active expert
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 13, color: overallColor, fontWeight: 700 }}>{overallStatus.replace('_', ' ')}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{percentComplete}% complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, marginBottom: 24, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percentComplete}%`, background: 'linear-gradient(90deg,var(--primary),var(--accent))', borderRadius: 99, transition: 'width 0.6s ease' }} />
      </div>

      {/* Stages */}
      <div style={{ position: 'relative' }}>
        {/* Vertical connector line */}
        <div style={{ position: 'absolute', left: 17, top: 24, bottom: 24, width: 2, background: 'var(--border)', zIndex: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {stages.map((s, i) => {
            const st = STATUS_STYLE[s.status] || STATUS_STYLE.PENDING;
            return (
              <div key={s.stage} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0', position: 'relative', zIndex: 1 }}>
                {/* Node */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: s.status === 'COMPLETE' ? '#10b981' : s.status === 'LOCKED' ? 'var(--surface-hover)' : st.bg,
                  border: `2px solid ${st.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14,
                }}>
                  {s.status === 'COMPLETE' ? '✓' : s.status === 'LOCKED' ? '🔒' : i + 1}
                </div>

                {/* Content */}
                <div style={{
                  flex: 1, background: 'var(--surface)', border: `1px solid ${s.status === 'IN_REVIEW' || s.status === 'IN_PROGRESS' ? st.color : 'var(--border)'}`,
                  borderRadius: 12, padding: '10px 14px',
                  opacity: s.status === 'LOCKED' ? 0.5 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 'var(--font-sm)' }}>{s.label}</span>
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 99, background: st.bg, color: st.color, fontWeight: 700 }}>
                      {s.status.replace('_', ' ')}
                    </span>
                  </div>
                  {s.details && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.details}</div>}
                  {s.completedAt && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {new Date(s.completedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
